/**
 * The concept module blocks.
 *
 * A concept module is the science and philosophy layer: affordances,
 * constraints, what a "read" actually is. Two versions of every concept ship
 * from one set of components — a Platform read of five to ten minutes that
 * should feel like an excellent article, and a Certification version of
 * twenty to twenty-five that asks the coach to do something.
 *
 * Three rules from the brief, enforced here rather than left to the author:
 *
 *   The Platform module never feels like school. Nothing in the Platform set
 *   below takes an answer, grades anything, or blocks the next section. The
 *   only quasi-interactive thing is `reflection`, which is a prompt you read
 *   and walk away from.
 *
 *   Nothing essential lives inside `scienceNerd`. It is collapsed by default
 *   and a coach who never opens one has still had the whole lesson. That is a
 *   promise the component cannot keep on its own, so it is also written on
 *   the block in the authoring files.
 *
 *   Written work saves to the account, not to the browser. A coach who fills
 *   in four diagnostic fields about a real player and then opens the module
 *   on their phone should find their own words there. The rest of the reader
 *   persists to localStorage; this doesn't, because this is the part worth
 *   coming back to.
 *
 * They share one renderer with the masterclasses, so a module can use any
 * existing block (text, pullquote, video, callout, principles, compare) and
 * these are additions rather than a second system.
 */
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, ArrowRight, AlertTriangle, Lightbulb, BookOpen, ExternalLink, Headphones } from "lucide-react";
import { GOLD } from "../constants/theme";
import { loadUserData, saveUserData } from "../utils/userData";
import { openExternal } from "../../utils/nativeBridge";
import { withLinks } from "./inlineMarkdown";

const ff = "'DM Sans', sans-serif";
const hf = "'Bebas Neue', sans-serif";

/* ── Written work, kept on the account ──────────────────────────────────
 *
 * One user_data row per module rather than one per answer: `user_data` is a
 * generic key/value table with an upsert on (user_id, key), so no schema
 * change was needed for any of this, and a module's worth of answers is one
 * round trip in and one out.
 *
 * Writes are debounced. A coach writing three sentences about a player
 * should not generate forty upserts.
 */
export function useModuleResponses(moduleKey) {
  const key = `concept_${moduleKey}`;
  const [map, setMap] = useState({});
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);
  const pending = useRef(null);

  useEffect(() => {
    let alive = true;
    loadUserData(key, {}).then(v => {
      if (!alive) return;
      setMap(v && typeof v === "object" && !Array.isArray(v) ? v : {});
      setReady(true);
    }).catch(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [key]);

  // Flush whatever is queued. Called on the debounce and again on unmount,
  // because closing the reader mid-sentence is the most likely way to leave.
  const flush = useRef(() => {});
  flush.current = () => {
    if (pending.current == null) return;
    const body = pending.current;
    pending.current = null;
    setSaving(true);
    saveUserData(key, body).catch(() => {}).finally(() => setSaving(false));
  };

  useEffect(() => () => { clearTimeout(timer.current); flush.current(); }, []);

  const set = (field, value) => {
    setMap(prev => {
      const next = { ...prev, [field]: value };
      pending.current = next;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => flush.current(), 700);
      return next;
    });
  };

  return { map, set, ready, saving };
}

/* A response field. `responses` is threaded down from the reader so every
   one of these writes into the same module row. Without it (a module opened
   outside the reader, or a preview) it degrades to an ordinary textarea
   rather than throwing. */
function Field({ C, responses, id, label, hint, rows = 3 }) {
  const value = responses?.map?.[id] ?? "";
  const [local, setLocal] = useState(value);
  // Adopt the stored value once it lands, but never over something the
  // coach has already started typing.
  const touched = useRef(false);
  useEffect(() => { if (!touched.current) setLocal(value); }, [value]);
  const onChange = (v) => { touched.current = true; setLocal(v); responses?.set?.(id, v); };
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      {label && (
        <span style={{ display: "block", fontSize: 12.5, fontWeight: 800, color: C.text,
          marginBottom: 5, lineHeight: 1.45 }}>{bold(label)}</span>
      )}
      {/* Hints are curriculum text and can carry lines, bullets and bold
          (concept modules are the source doc's words, verbatim). */}
      {hint && (
        <span style={{ display: "block", marginBottom: 6 }}>
          {String(hint).split("\n").filter(l => l.trim()).map((l, i) => {
            const t = l.trim();
            const item = t.startsWith("- ");
            return (
              <span key={i} style={{ display: "block", fontSize: 12.5, color: C.textDim, lineHeight: 1.5,
                margin: "0 0 3px", paddingLeft: item ? 14 : 0, position: "relative" }}>
                {item && <span aria-hidden="true" style={{ position: "absolute", left: 3 }}>•</span>}
                {bold(item ? t.slice(2) : t)}
              </span>
            );
          })}
        </span>
      )}
      <textarea value={local} rows={rows} onChange={e => onChange(e.target.value)}
        placeholder="Your answer"
        style={{ width: "100%", padding: "10px 12px", borderRadius: 9, resize: "vertical",
          border: `1px solid ${C.border}`, background: C.inputBg, color: C.text,
          fontSize: 14, lineHeight: 1.6, fontFamily: ff, outline: "none" }}/>
    </label>
  );
}

/* Reveal. Certification can ask for an answer before it shows its own, and
   the brief is explicit that what comes back is reasoning rather than a
   verdict, so the button says discussion and not "answer". */
function Reveal({ C, children, label = "Reveal the BAM discussion" }) {
  const [open, setOpen] = useState(false);
  if (!children) return null;
  if (!open) {
    return (
      <button type="button" className="btn" onClick={() => setOpen(true)}
        style={{ padding: "10px 16px", borderRadius: 9, border: `1px solid ${GOLD}66`,
          background: `${GOLD}12`, color: GOLD, fontSize: 13, fontWeight: 800,
          fontFamily: ff, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}>
        {label} <ArrowRight size={14}/>
      </button>
    );
  }
  return (
    <div style={{ marginTop: 4, paddingLeft: 14, borderLeft: `2px solid ${GOLD}66` }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase",
        color: GOLD, marginBottom: 7 }}>BAM discussion</div>
      {children}
    </div>
  );
}

/* A block's outer shell. Cards are used only where they create hierarchy:
   the brief asks for editorial prose interrupted by a few things that
   matter, not fifty stacked cards. */
function Panel({ C, accent, children, style }) {
  return (
    <div style={{ margin: "22px 0", padding: "18px 20px", borderRadius: 13,
      border: `1px solid ${accent ? `${GOLD}44` : C.cardBorder}`,
      background: accent ? `linear-gradient(135deg, ${GOLD}12, ${GOLD}04)` : C.cardGradient,
      ...style }}>
      {children}
    </div>
  );
}

function Eyebrow({ C, children, tone }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase",
      color: tone || GOLD, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
      {children}
    </div>
  );
}

/* ── Platform ─────────────────────────────────────────────────────────── */

/* The one sentence a coach should leave with. Set large, no container of its
   own beyond a rule, so it interrupts the article the way a held note
   interrupts a phrase. */
export function BigIdea({ C, md }) {
  return (
    <div style={{ margin: "30px 0", paddingLeft: 18, borderLeft: `3px solid ${GOLD}` }}>
      <div style={{ fontFamily: hf, fontSize: "clamp(23px, 3.2vw, 31px)", letterSpacing: 1.1,
        lineHeight: 1.18, color: C.text, textTransform: "none" }}>{md}</div>
    </div>
  );
}

/* An example. The label is the author's, because the brief uses this one
   component for everyday examples, coaching examples and player scenarios,
   and the three read very differently. */
export function Scenario({ C, label, title, md, muxPlaybackId }) {
  return (
    <Panel C={C}>
      {label && <Eyebrow C={C}>{label}</Eyebrow>}
      {title && (
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 8,
          lineHeight: 1.35 }}>{title}</div>
      )}
      {muxPlaybackId && (
        <div style={{ position: "relative", aspectRatio: "16/9", borderRadius: 9,
          overflow: "hidden", background: "#000", marginBottom: 12 }}>
          <img alt="" src={`https://image.mux.com/${muxPlaybackId}/thumbnail.jpg?width=900&height=506&fit_mode=smartcrop`}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}/>
        </div>
      )}
      <Prose C={C} md={md}/>
    </Panel>
  );
}

/* Where the general science stops and our reading of it starts. Marked,
   because the brief is firm that the two should not blur: an applied
   interpretation presented as a finding is how a framework stops being
   honest. */
export function BamInsight({ C, title, md }) {
  return (
    <Panel C={C} accent>
      <Eyebrow C={C}><Lightbulb size={12}/> {title || "The BAM read"}</Eyebrow>
      <Prose C={C} md={md}/>
    </Panel>
  );
}

/* The chain. CAN THEY DO IT, then the next, then the next. Built to be
   remembered and to stack cleanly on a phone, which rules out the horizontal
   flow diagram this wants to be on a laptop. */
export function Framework({ C, title, steps = [] }) {
  return (
    <Panel C={C} accent style={{ padding: "20px 20px 16px" }}>
      {title && <Eyebrow C={C}>{title}</Eyebrow>}
      {steps.map((s, i) => (
        <div key={i}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <span style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%",
              background: GOLD, color: "#111", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 12.5, fontWeight: 800,
              fontVariantNumeric: "tabular-nums" }}>{i + 1}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontFamily: hf, fontSize: 21, letterSpacing: 1.2, lineHeight: 1.1,
                color: C.text, textTransform: "uppercase" }}>{s.q}</div>
              {s.md && (
                <div style={{ fontSize: 13.5, color: C.textMid, lineHeight: 1.6, marginTop: 5,
                  whiteSpace: "pre-line" }}>{s.md}</div>
              )}
            </div>
          </div>
          {i < steps.length - 1 && (
            <div aria-hidden="true" style={{ height: 18, marginLeft: 12,
              borderLeft: `2px solid ${GOLD}4D`, margin: "7px 0 7px 12px" }}/>
          )}
        </div>
      ))}
    </Panel>
  );
}

/* What people think, and what is actually true. Two halves rather than a
   paragraph, because the point is the gap between them. */
export function Misconception({ C, myth, truth }) {
  return (
    <Panel C={C}>
      <div style={{ marginBottom: 12 }}>
        <Eyebrow C={C} tone="#E08787"><AlertTriangle size={12}/> The misconception</Eyebrow>
        <div style={{ fontSize: 14.5, color: C.text, lineHeight: 1.6, fontWeight: 600 }}>{myth}</div>
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
        <Eyebrow C={C}><Check size={12}/> What it actually says</Eyebrow>
        <Prose C={C} md={truth}/>
      </div>
    </Panel>
  );
}

/* Think About It. The only interactive thing in a Platform module, and it
   is not really interactive: no field, no submit, nothing to get right.
   A question you carry out of the page. */
export function Reflection({ C, title, md }) {
  return (
    <div style={{ margin: "26px 0", padding: "18px 20px", borderRadius: 13,
      border: `1px dashed ${GOLD}66`, background: `${GOLD}09` }}>
      <Eyebrow C={C}>{title || "Think about it"}</Eyebrow>
      <Prose C={C} md={md} size={15}/>
    </div>
  );
}

export function RelatedConcepts({ C, title, items = [], onOpen }) {
  if (!items.length) return null;
  return (
    <div style={{ margin: "30px 0 8px", paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
      <Eyebrow C={C}>{title || "Related concepts"}</Eyebrow>
      <div style={{ display: "grid", gap: 9,
        gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))" }}>
        {items.map(it => (
          <button key={it.key} type="button" className="btn"
            onClick={() => onOpen?.(it.key)}
            disabled={!onOpen || it.soon}
            style={{ textAlign: "left", padding: "12px 14px", borderRadius: 11,
              border: `1px solid ${C.border}`, background: C.cardGradient, color: C.text,
              fontFamily: ff, cursor: onOpen && !it.soon ? "pointer" : "default",
              opacity: it.soon ? 0.5 : 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 700, flex: 1, minWidth: 0 }}>{it.title}</span>
              {it.soon && (
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.9,
                  textTransform: "uppercase", color: C.textDim }}>Soon</span>
              )}
            </div>
            {it.blurb && (
              <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.45 }}>{it.blurb}</div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Certification ────────────────────────────────────────────────────── */

/* Material that only exists in the Certification. Marked so a coach can see
   what the cert added rather than wondering whether they misread the
   Platform version. */
export function CertDepth({ C, title, children }) {
  return (
    <div style={{ margin: "26px 0", paddingLeft: 16, borderLeft: `2px solid ${GOLD}55` }}>
      <Eyebrow C={C}>Certification deep dive</Eyebrow>
      {title && (
        <div style={{ fontFamily: hf, fontSize: 24, letterSpacing: 1.3, lineHeight: 1.1,
          textTransform: "uppercase", color: C.text, marginBottom: 10 }}>{title}</div>
      )}
      {children}
    </div>
  );
}

/* For the science nerds. Collapsed, always. Everything in here is optional
   by construction: methodology, exact numbers, the theoretical argument. A
   coach who never opens one has still had the entire lesson, and the
   authoring files say so on every block so the next person writing one
   knows it is a rule rather than a habit. */
export function ScienceNerd({ C, children, title }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ margin: "20px 0", borderRadius: 12, overflow: "hidden",
      border: `1px solid ${C.border}`, background: C.cardGradient }}>
      <button type="button" className="btn" onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{ width: "100%", textAlign: "left", padding: "13px 16px", background: "transparent",
          border: "none", cursor: "pointer", fontFamily: ff, color: C.text,
          display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 11, fontWeight: 800, letterSpacing: 1.4,
            textTransform: "uppercase", color: GOLD }}>
            For the science nerds <span aria-hidden="true">🤓</span>
          </span>
          {/* The doc's own title for this one, when it gives one. */}
          {title && <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: C.text,
            marginTop: 4, lineHeight: 1.4 }}>{title}</span>}
        </span>
        <span style={{ fontSize: 11.5, color: C.textDim, fontWeight: 700 }}>
          {open ? "Hide" : "Optional"}
        </span>
        <ChevronDown size={15} color={C.textDim}
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .18s ease" }}/>
      </button>
      {open && (
        <div style={{ padding: "2px 16px 14px" }}>{children}</div>
      )}
    </div>
  );
}

/* A study, kept small. Author, year, what they did, what coaches should take
   from it. Deliberately not a table: the brief bans tiny research tables on
   phones and this is going to be read on a phone. */
export function ResearchCard({ C, author, year, title, sample, found, why, url }) {
  return (
    <Panel C={C}>
      <Eyebrow C={C}><BookOpen size={12}/> Research</Eyebrow>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text, lineHeight: 1.4,
        marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: C.textDim, marginBottom: 11 }}>
        {[author, year].filter(Boolean).join(", ")}
      </div>
      {sample && (
        <Row C={C} label="What they did" body={sample}/>
      )}
      {found && <Row C={C} label="What they found" body={found}/>}
      {why && <Row C={C} label="Why it matters to you" body={why}/>}
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 4,
            fontSize: 12.5, fontWeight: 700, color: GOLD, textDecoration: "none" }}>
          Source <ExternalLink size={12}/>
        </a>
      )}
    </Panel>
  );
}

function Row({ C, label, body }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase",
        color: C.textDim, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: C.textMid, lineHeight: 1.6 }}>{body}</div>
    </div>
  );
}

/* A case study with options. There is no correct/incorrect popup: the coach
   commits, then reads why one reading is better than the others. The
   selected option is remembered on the account like every other answer. */
export function MultipleChoice({ C, responses, id, prompt, options = [], discussion }) {
  const picked = responses?.map?.[id] ?? null;
  return (
    <Panel C={C}>
      <Eyebrow C={C}>Your call</Eyebrow>
      <Prose C={C} md={prompt}/>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "12px 0 14px" }}>
        {options.map(o => {
          const on = picked === o.id;
          return (
            <button key={o.id} type="button" className="btn"
              onClick={() => responses?.set?.(id, on ? null : o.id)}
              style={{ textAlign: "left", padding: "12px 14px", borderRadius: 10,
                border: `1px solid ${on ? GOLD : C.border}`,
                background: on ? `${GOLD}14` : "transparent", color: C.text,
                fontFamily: ff, fontSize: 13.5, lineHeight: 1.55, cursor: "pointer",
                display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span style={{ flexShrink: 0, marginTop: 1, width: 19, height: 19, borderRadius: "50%",
                border: `1.5px solid ${on ? GOLD : C.border}`, background: on ? GOLD : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                {on && <Check size={12} color="#111" strokeWidth={3}/>}
              </span>
              <span style={{ flex: 1 }}>{bold(o.label)}</span>
            </button>
          );
        })}
      </div>
      {picked && <Reveal C={C}><Prose C={C} md={discussion}/></Reveal>}
    </Panel>
  );
}

/* Write first, then read ours. The reveal is not gated on the text being
   there — a coach who wants to think it rather than type it should not be
   locked out — but it sits below the field so the order is obvious. */
export function WrittenResponse({ C, responses, id, prompt, hint, discussion }) {
  return (
    <Panel C={C}>
      <Eyebrow C={C}>Write it down</Eyebrow>
      <Field C={C} responses={responses} id={id} label={prompt} hint={hint} rows={4}/>
      {discussion && <Reveal C={C}><Prose C={C} md={discussion}/></Reveal>}
    </Panel>
  );
}

/* The four questions, as an exercise rather than a diagram. Same chain as
   the `framework` block, but here the coach fills it in. */
export function Diagnostic({ C, responses, id, title, intro, outro, prompts = [], discussion }) {
  return (
    <Panel C={C} accent>
      <Eyebrow C={C}>{title || "Work the check"}</Eyebrow>
      {intro && <Prose C={C} md={intro}/>}
      <div style={{ marginTop: 12 }}>
        {prompts.map(p => (
          <Field key={p.id} C={C} responses={responses} id={`${id}.${p.id}`}
            label={p.q} hint={p.hint} rows={3}/>
        ))}
      </div>
      {outro && <Prose C={C} md={outro}/>}
      {discussion && <Reveal C={C}><Prose C={C} md={discussion}/></Reveal>}
    </Panel>
  );
}

/* Apply it to your player. The recurring Certification component, and the
   reason responses live on the account: this is the one a coach comes back
   to in three weeks to see what they thought the first time. */
export function ApplyToAthlete({ C, responses, id, title, intro, steps = [], numbered = true, discussion }) {
  return (
    <Panel C={C} accent>
      {/* title: false when the section heading above already names it. */}
      {title !== false && <Eyebrow C={C}>{title || "Apply it to your player"}</Eyebrow>}
      {intro && <Prose C={C} md={intro}/>}
      <div style={{ marginTop: 12 }}>
        {steps.map((s, i) => (
          <Field key={s.id} C={C} responses={responses} id={`${id}.${s.id}`}
            label={numbered ? `${i + 1}. ${s.q}` : s.q} hint={s.hint} rows={s.rows || 3}/>
        ))}
      </div>
      {/* When his activity ends on a BAM Discussion, it opens after the steps. */}
      {discussion && <div style={{ marginBottom: 12 }}><Reveal C={C}><Prose C={C} md={discussion}/></Reveal></div>}
      {responses?.ready && (
        <div style={{ fontSize: 11.5, color: C.textDim, display: "flex", alignItems: "center", gap: 6 }}>
          <Check size={12} color={GOLD}/> Saved to your account. Come back to it whenever.
        </div>
      )}
    </Panel>
  );
}

/* ── A masterclass link ───────────────────────────────────────────────── */
/* A concept pointing at the masterclass that teaches the rest (Differential
   Learning's shooting section, at Coleman's note, links Building Better
   Shooters rather than re-teaching it). Opens it in Masterclasses, the same
   hand-off other surfaces use. */
export function MasterclassLink({ C, moduleKey, title }) {
  const open = () => {
    try { sessionStorage.setItem("bam_open_module", moduleKey); } catch { /* private window */ }
    window.location.hash = "#/master";
  };
  return <LinkCard C={C} eyebrow="Masterclass" title={title} icon={BookOpen} onClick={open}/>;
}

/* A podcast episode to go with a concept (Degrees of Freedom links his
   team episode). Opens through openExternal, the same path the
   community's links use. */
export function PodcastLink({ C, title, url }) {
  return <LinkCard C={C} eyebrow="Podcast" title={title} icon={Headphones} external
    onClick={() => openExternal(url)}/>;
}

function LinkCard({ C, eyebrow, title, icon: Icon, onClick, external = false }) {
  return (
    <button type="button" className="btn" onClick={onClick}
      style={{ width: "100%", textAlign: "left", margin: "6px 0 22px", padding: "14px 16px", borderRadius: 12,
        border: `1px solid ${GOLD}55`, background: `${GOLD}0d`, color: C.text, fontFamily: ff, cursor: "pointer",
        display: "flex", alignItems: "center", gap: 12, minHeight: 44 }}>
      <Icon size={18} color={GOLD} style={{ flexShrink: 0 }}/>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 10.5, fontWeight: 800, letterSpacing: 1.3,
          textTransform: "uppercase", color: GOLD, marginBottom: 2 }}>{eyebrow}</span>
        <span style={{ display: "block", fontSize: 15, fontWeight: 700 }}>{title}</span>
      </span>
      {external ? <ExternalLink size={16} color={GOLD} style={{ flexShrink: 0 }}/> : <ArrowRight size={16} color={GOLD} style={{ flexShrink: 0 }}/>}
    </button>
  );
}

/* ── Timed rounds ─────────────────────────────────────────────────────── */
/* A demonstration, not a test (Types of Variability, "Try This"). Each
   problem shows one at a time and moves on the moment the answer is right;
   each round reports its average response time, which is the doc's own
   ask ("the platform can measure average response time for each round").
   Nothing is saved: it's a feeling to have, not a score to keep. */
const answerOf = (problem) => {
  const m = String(problem).match(/(\d+)\s*[×x*]\s*(\d+)/);
  return m ? Number(m[1]) * Number(m[2]) : null;
};
const secs = (ms) => `${(ms / 1000).toFixed(1)}s`;

export function TimedRounds({ C, rounds = [] }) {
  const [round, setRound] = useState(0);       // which round is up
  const [step, setStep] = useState(-1);        // -1 = not started
  const [value, setValue] = useState("");
  const [miss, setMiss] = useState(false);
  const [times, setTimes] = useState([]);      // [[ms, ...], ...]
  const shownAt = useRef(0);
  const input = useRef(null);
  const cur = rounds[round];
  const done = round >= rounds.length;

  useEffect(() => {
    if (step < 0 || done) return;
    shownAt.current = performance.now();
    input.current?.focus();
  }, [step, round, done]);

  const start = () => { setStep(0); setValue(""); };
  const onType = (v) => {
    const digits = v.replace(/\D/g, "");
    const want = answerOf(cur.problems[step]);
    setMiss(false);
    if (Number(digits) === want && digits.length === String(want).length) {
      const took = performance.now() - shownAt.current;
      setTimes(t => { const n = [...t]; n[round] = [...(n[round] || []), took]; return n; });
      setValue("");
      if (step + 1 < cur.problems.length) setStep(step + 1);
      else { setRound(round + 1); setStep(-1); }
      return;
    }
    if (digits.length >= String(want).length) { setMiss(true); setValue(""); return; }
    setValue(digits);
  };
  const reset = () => { setRound(0); setStep(-1); setTimes([]); setValue(""); setMiss(false); };

  const avg = (i) => times[i]?.length ? times[i].reduce((a, b) => a + b, 0) / times[i].length : null;
  const slowest = Math.max(...rounds.map((_, i) => avg(i) || 0), 1);
  const btn = { minHeight: 44, padding: "0 18px", borderRadius: 10, border: "none", background: GOLD, color: "#111",
    fontSize: 14, fontWeight: 800, fontFamily: ff, cursor: "pointer" };

  return (
    <Panel C={C} accent>
      {/* Averages so far, one bar per finished round. */}
      {times.length > 0 && (
        <div style={{ marginBottom: done ? 4 : 16 }}>
          <Eyebrow C={C}>Average response time</Eyebrow>
          {rounds.map((r, i) => avg(i) != null && (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{ width: 92, flexShrink: 0, fontSize: 12.5, color: C.textMid }}>{r.label.replace(/^Now\s+|:$/g, "")}</span>
              <span style={{ flex: 1, height: 10, borderRadius: 5, background: C.border, overflow: "hidden" }}>
                <span style={{ display: "block", height: "100%", width: `${(avg(i) / slowest) * 100}%`,
                  background: i ? GOLD : `${GOLD}88`, borderRadius: 5 }}/>
              </span>
              <span style={{ width: 44, textAlign: "right", fontSize: 13, fontWeight: 800, color: C.text,
                fontVariantNumeric: "tabular-nums" }}>{secs(avg(i))}</span>
            </div>
          ))}
        </div>
      )}
      {!done && (
        <>
          <div style={{ fontFamily: hf, fontSize: 22, letterSpacing: 1.2, color: C.text, marginBottom: 10 }}>{cur.label}</div>
          {step < 0 ? (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                {cur.problems.map((p, i) => (
                  <span key={i} style={{ fontSize: 13.5, fontWeight: 700, color: C.textMid, padding: "6px 10px",
                    borderRadius: 8, border: `1px solid ${C.border}` }}>{p}</span>
                ))}
              </div>
              <button type="button" className="btn" onClick={start} style={btn}>Start</button>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <span style={{ fontFamily: hf, fontSize: 44, letterSpacing: 1.5, color: C.text, lineHeight: 1,
                fontVariantNumeric: "tabular-nums" }}>{cur.problems[step].replace(/\?\s*$/, "")}</span>
              <input ref={input} value={value} onChange={e => onType(e.target.value)} inputMode="numeric"
                pattern="[0-9]*" autoComplete="off" aria-label={`Answer to ${cur.problems[step]}`}
                style={{ width: 92, minHeight: 52, fontSize: 28, fontWeight: 800, textAlign: "center", borderRadius: 10,
                  border: `1.5px solid ${miss ? "#E08787" : GOLD}`, background: "transparent", color: C.text,
                  fontFamily: ff, outline: "none" }}/>
              <span style={{ fontSize: 12, color: C.textDim, fontVariantNumeric: "tabular-nums" }}>
                {step + 1} / {cur.problems.length}
              </span>
            </div>
          )}
        </>
      )}
      {done && (
        <button type="button" className="btn" onClick={reset}
          style={{ ...btn, background: "transparent", color: C.textMid, border: `1px solid ${C.border}`, marginTop: 8 }}>
          Try again
        </button>
      )}
    </Panel>
  );
}

/* ── Table ───────────────────────────────────────────────────────────── */
/* A concept doc's markdown table (Types of Constraints' How × What grid),
   his cells as written. Wide, so on a phone it scrolls sideways inside its
   own box rather than pushing the page; the first column stays put so a
   row keeps its name while you scroll. */
export function ConceptTable({ C, head = [], rows = [] }) {
  const cell = { padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5, verticalAlign: "top",
    borderBottom: `1px solid ${C.border}`, textAlign: "left" };
  const sticky = { position: "sticky", left: 0, background: C.bgCard || C.bg, zIndex: 1 };
  return (
    <div style={{ margin: "6px 0 22px", overflowX: "auto", WebkitOverflowScrolling: "touch",
      border: `1px solid ${C.border}`, borderRadius: 12 }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i} scope="col" style={{ ...cell, ...(i === 0 ? sticky : null), fontSize: 11, fontWeight: 800,
                letterSpacing: .4, color: GOLD, whiteSpace: i === 0 ? "nowrap" : "normal" }}>{bold(h)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {(r.cells || []).map((c, ci) => ci === 0
                ? <th key={ci} scope="row" style={{ ...cell, ...sticky, color: C.text, fontWeight: 700, whiteSpace: "nowrap" }}>{bold(c)}</th>
                : <td key={ci} style={{ ...cell, color: C.textMid }}>{bold(c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Prose ────────────────────────────────────────────────────────────── */
/* Paragraphs, bullets and **bold**, which is all these modules use. The
   reader's own Paragraph does more, but it isn't exported, and duplicating
   three rules is cheaper than widening its surface for blocks that nest. */
export function Prose({ C, md, size = 14.5 }) {
  if (!md) return null;
  const blocks = String(md).split("\n").filter(l => l.trim() !== "");
  const out = [];
  let bullets = [];
  const flush = () => {
    if (!bullets.length) return;
    out.push(
      <ul key={out.length} style={{ margin: "0 0 12px", paddingLeft: 20 }}>
        {bullets.map((b, i) => (
          <li key={i} style={{ fontSize: size, lineHeight: 1.7, color: C.textMid, marginBottom: 5 }}>
            {bold(b)}
          </li>
        ))}
      </ul>
    );
    bullets = [];
  };
  blocks.forEach(line => {
    const t = line.trim();
    if (t.startsWith("- ")) { bullets.push(t.slice(2)); return; }
    flush();
    out.push(
      <p key={out.length} style={{ fontSize: size, lineHeight: 1.75, color: C.textMid,
        margin: "0 0 12px" }}>{bold(t)}</p>
    );
  });
  flush();
  return <>{out}</>;
}

function bold(s) {
  const parts = String(s).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith("**") && p.endsWith("**")
    ? <strong key={i} style={{ fontWeight: 800 }}>{p.slice(2, -2)}</strong>
    : <React.Fragment key={i}>{withLinks(p, `p${i}`, (t, k) => [<React.Fragment key={k}>{t}</React.Fragment>])}</React.Fragment>);
}
